/**
 * Add one source: name, publisher, link and an optional PDF.
 * Without a file the worker downloads the link (specs/dataset-workspace.md).
 */
import React, { useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { Input, Label, Button, Actions, StatusLine, FieldHelp } from '../../../components/InstanceSettings/formPrimitives';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { addSource } from './datasetApi';
import { Section, SectionTitle, FieldRow } from './styles';

export default function AddSourceForm({ datasetId, onAdded }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [title, setTitle] = useState('');
  const [publisher, setPublisher] = useState('');
  const [link, setLink] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await addSource(datasetId, { title, publisher, link, file });
      setTitle('');
      setPublisher('');
      setLink('');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      onAdded();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section as="form" onSubmit={handleSubmit}>
      <SectionTitle><Plus size={18} /> {t('datasetWorkspace.addSource')}</SectionTitle>
      <FieldRow>
        <div>
          <Label htmlFor="src-title">{t('datasetWorkspace.sourceName')}</Label>
          <Input id="src-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="src-publisher">{t('datasetWorkspace.publisher')}</Label>
          <Input id="src-publisher" value={publisher} onChange={(e) => setPublisher(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="src-link">{t('datasetWorkspace.link')}</Label>
          <Input id="src-link" type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://" />
        </div>
      </FieldRow>
      <Label htmlFor="src-file">{t('datasetWorkspace.file')}</Label>
      <input id="src-file" ref={fileRef} type="file" accept=".pdf,.txt,.md,.json,application/pdf"
        onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ color: '#ccc' }} />
      <FieldHelp>{t('datasetWorkspace.fileHelp')}</FieldHelp>
      <Actions>
        <Button type="submit" $variant="primary" disabled={saving || (!title.trim() && !file)}>
          {t('datasetWorkspace.add')}
        </Button>
        {error && <StatusLine $error>{error}</StatusLine>}
      </Actions>
    </Section>
  );
}
